import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { TsButtonModule, TSIconModule } from 'tsui';
import { DecimalPipe, DOCUMENT, JsonPipe, NgClass } from '@angular/common';
import { Dialog } from '@angular/cdk/dialog';
import {
  ManageTaskPredecessorComponent,
  ManageTaskPredecessorsData,
} from '@tasks/task-details/manage-task-predecessors/manage-task-predecessor.component';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { filter, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { Predecessor, Task } from '@tasks/tasks.types';
import { MatDividerModule } from '@angular/material/divider';

@Component({
  selector: 'task-predecessors',
  standalone: true,
  imports: [TsButtonModule, TSIconModule, JsonPipe, NgClass, DecimalPipe, MatDividerModule],
  templateUrl: './task-predecessors.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskPredecessorsComponent {
  private dialog = inject(Dialog);
  private docRef = inject(DOCUMENT);
  @Input() disabled: boolean;
  @Input() rootTask: Task;
  @Input() taskPredecessorOptions: Task[];
  @Input() canEdit = true;

  @Output() navigateToTask = new EventEmitter<number>();
  @Output() predecessorsUpdated = new EventEmitter<Predecessor[]>();

  get predecessors(): Predecessor[] {
    return this.rootTask?.predecessors || [];
  }

  onManagePredecessors(
    mode: 'add' | 'edit' | 'delete',
    taskPredecessorOptions?: Task[],
    selectedPredecessor?: Predecessor,
  ) {
    if (!this.canEdit) {
      return;
    }

    const dialogRef = this.dialog.open(ManageTaskPredecessorComponent, {
      data: <ManageTaskPredecessorsData>{
        rootTask: this.rootTask,
        selectedPredecessor,
        taskPredecessorOptions,
        mode,
      },
      ...TsDialogComponent.getDefaultDialogConfig(),
    });

    dialogRef.closed
      .pipe(
        tap(() => {
          (this.docRef.activeElement as HTMLButtonElement).blur();
        }),
        filter((result) => !!result),
        map((predecessor: Predecessor) => {
          if (mode === 'delete') {
            return this.predecessors.filter((p) => p.id !== predecessor.id);
          }

          return this.updatePredecessor(predecessor, this.predecessors);
        }),
      )
      .subscribe((result) => {
        this.predecessorsUpdated.emit(result);
      });
  }

  onNavigateToTask($event: Event, predecessor_task_id: number) {
    $event.stopPropagation();
    this.navigateToTask.emit(predecessor_task_id);
  }

  onRemovePredecessor($event: MouseEvent, taskPredecessorOptions: Task[], selectedPredecessor: Predecessor) {
    $event.stopPropagation();
    this.onManagePredecessors('delete', taskPredecessorOptions, selectedPredecessor);
  }

  private updatePredecessor(updated: Predecessor, predecessorsList: Predecessor[]): Predecessor[] {
    let result = [...predecessorsList];

    const updatedPredecessorIndex = predecessorsList.findIndex((predecessor) => predecessor.id === updated.id);

    if (updatedPredecessorIndex === -1) {
      result = [...result, updated];
    } else {
      result[updatedPredecessorIndex] = updated;
    }

    return result;
  }
}
